import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  Bird, Fish, Squirrel, Rabbit, Shell, TreePine, Leaf, GraduationCap, Users, Globe2,
  HeartHandshake, Sparkles, BookOpen, Award, Calculator, Plane, Car, Building2,
  ChevronRight, MapPin, Calendar, Target, TrendingUp, Wind, Search,
} from "lucide-react";
import whaleImg from "@/assets/verde-whale.webp";
import mangroveImg from "@/assets/verde-mangrove.webp";
import pineImg from "@/assets/verde-pine.webp";
import plantingImg from "@/assets/verde-planting.webp";
import heroImg from "@/assets/verde-hero.webp";
import agaveImg from "@/assets/verde-agave.webp";

/* ============================ FAUNA CATALOG ============================ */

const FAUNA = [
  { id: "jaguar", common: "Jaguar", sci: "Panthera onca", group: "Mamífero", state: "Chiapas", status: "En peligro", img: pineImg, icon: Squirrel },
  { id: "quetzal", common: "Quetzal", sci: "Pharomachrus mocinno", group: "Ave", state: "Chiapas", status: "Amenazado", img: heroImg, icon: Bird },
  { id: "ballena-gris", common: "Ballena gris", sci: "Eschrichtius robustus", group: "Marino", state: "Baja California Sur", status: "Protegida", img: whaleImg, icon: Fish },
  { id: "tortuga-golfina", common: "Tortuga golfina", sci: "Lepidochelys olivacea", group: "Marino", state: "Oaxaca", status: "Vulnerable", img: mangroveImg, icon: Shell },
  { id: "ajolote", common: "Ajolote", sci: "Ambystoma mexicanum", group: "Anfibio", state: "CDMX", status: "Crítico", img: heroImg, icon: Fish },
  { id: "manati", common: "Manatí", sci: "Trichechus manatus", group: "Marino", state: "Quintana Roo", status: "En peligro", img: whaleImg, icon: Fish },
  { id: "flamenco", common: "Flamenco rosado", sci: "Phoenicopterus ruber", group: "Ave", state: "Yucatán", status: "Protegida", img: mangroveImg, icon: Bird },
  { id: "vaquita", common: "Vaquita marina", sci: "Phocoena sinus", group: "Marino", state: "Baja California", status: "Crítico", img: whaleImg, icon: Fish },
  { id: "lobo-mexicano", common: "Lobo mexicano", sci: "Canis lupus baileyi", group: "Mamífero", state: "Chihuahua", status: "Crítico", img: pineImg, icon: Rabbit },
  { id: "guacamaya", common: "Guacamaya roja", sci: "Ara macao", group: "Ave", state: "Chiapas", status: "Amenazado", img: heroImg, icon: Bird },
  { id: "mono-aullador", common: "Mono aullador", sci: "Alouatta palliata", group: "Mamífero", state: "Veracruz", status: "Vulnerable", img: pineImg, icon: Squirrel },
  { id: "tiburon-ballena", common: "Tiburón ballena", sci: "Rhincodon typus", group: "Marino", state: "Quintana Roo", status: "En peligro", img: whaleImg, icon: Fish },
];

const FAUNA_GROUPS = ["Todos", "Mamífero", "Ave", "Marino", "Anfibio"] as const;

export function FaunaCatalog() {
  const [q, setQ] = useState("");
  const [group, setGroup] = useState<(typeof FAUNA_GROUPS)[number]>("Todos");

  const items = useMemo(() => {
    const term = q.trim().toLowerCase();
    return FAUNA.filter((f) => {
      const matchQ = !term || f.common.toLowerCase().includes(term) || f.sci.toLowerCase().includes(term) || f.state.toLowerCase().includes(term);
      const matchG = group === "Todos" || f.group === group;
      return matchQ && matchG;
    });
  }, [q, group]);

  return (
    <section className="bg-emerald-50/40 py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-8 max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-emerald-800">
            <Bird className="h-3 w-3" /> México Verde — Fauna
          </div>
          <h2 className="mt-4 font-display text-3xl font-black text-emerald-950 sm:text-5xl">Fauna de México</h2>
          <p className="mt-3 text-foreground/70">Más de 1,000 especies registradas. Mamíferos, aves, anfibios y vida marina protegida.</p>
        </div>

        <div className="mb-5 grid gap-3 sm:grid-cols-[1fr_auto]">
          <div className="flex items-center gap-2 rounded-full border border-emerald-200 bg-white px-4 py-2.5">
            <Search className="h-4 w-4 text-emerald-700" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Busca por nombre, especie o estado"
              className="w-full bg-transparent text-sm outline-none placeholder:text-emerald-700/50"
              aria-label="Buscar fauna"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {FAUNA_GROUPS.map((g) => (
              <button
                key={g}
                onClick={() => setGroup(g)}
                className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${
                  group === g ? "border-emerald-700 bg-emerald-700 text-white" : "border-emerald-200 bg-white text-emerald-800 hover:bg-emerald-50"
                }`}
              >
                {g}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((f) => {
            const Icon = f.icon;
            return (
              <article key={f.id} className="group overflow-hidden rounded-3xl border border-emerald-100 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl">
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img src={f.img} alt={f.common} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-emerald-800">
                    <Icon className="h-3 w-3" /> {f.group}
                  </div>
                  <div className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-rose-600/90 px-2.5 py-1 text-[10px] font-semibold text-white">
                    {f.status}
                  </div>
                </div>
                <div className="p-4">
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-emerald-700">{f.state}</div>
                  <h3 className="mt-1 font-display text-lg font-black text-emerald-950">{f.common}</h3>
                  <p className="text-xs italic text-foreground/60">{f.sci}</p>
                </div>
              </article>
            );
          })}
          {items.length === 0 && (
            <div className="col-span-full rounded-3xl border border-dashed border-emerald-200 p-10 text-center text-sm text-foreground/60">
              Sin resultados para esa búsqueda.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/* ============================ CONSERVATION PROJECTS ============================ */

const PROJECTS = [
  { id: "lacandona", title: "Restauración Selva Lacandona", state: "Chiapas", goal: 500000, raised: 312500, supporters: 1284, img: heroImg, partner: "Conservación Internacional", desc: "Reforestación de 1,200 hectáreas y monitoreo de jaguar." },
  { id: "manglares", title: "Manglares de Sian Ka'an", state: "Quintana Roo", goal: 350000, raised: 198000, supporters: 942, img: mangroveImg, partner: "Pronatura Península de Yucatán", desc: "Rehabilitación de 80 km de manglar costero." },
  { id: "ballena-gris", title: "Santuario Ballena Gris", state: "Baja California Sur", goal: 220000, raised: 156000, supporters: 612, img: whaleImg, partner: "WWF México", desc: "Protección de zonas de reproducción y monitoreo satelital." },
  { id: "vaquita", title: "Salvemos a la Vaquita", state: "Baja California", goal: 800000, raised: 240000, supporters: 1820, img: whaleImg, partner: "Sea Shepherd", desc: "Patrullaje marítimo y retiro de redes fantasma en el Alto Golfo." },
  { id: "pino-oaxaca", title: "Bosques de Pino Oaxaqueño", state: "Oaxaca", goal: 180000, raised: 92000, supporters: 487, img: pineImg, partner: "Reforestamos México", desc: "Reforestación con especies endémicas y manejo comunitario." },
  { id: "ajolote", title: "Refugio del Ajolote", state: "CDMX", goal: 120000, raised: 88000, supporters: 1102, img: plantingImg, partner: "UNAM Biología", desc: "Restauración de chinampas en Xochimilco y cría en cautiverio." },
];

export function ConservationProjects() {
  return (
    <section id="proyectos" className="bg-white py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-emerald-800">
              <Target className="h-3 w-3" /> Proyectos de conservación
            </div>
            <h2 className="mt-4 font-display text-3xl font-black text-emerald-950 sm:text-5xl">Apoya proyectos reales</h2>
            <p className="mt-3 text-foreground/70">Cada peso financiado va directo a organizaciones verificadas que protegen ecosistemas mexicanos.</p>
          </div>
          <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-5 py-3 text-sm text-emerald-900">
            <strong className="text-emerald-950">100%</strong> transparencia · auditoría anual
          </div>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {PROJECTS.map((p) => {
            const pct = Math.min(100, Math.round((p.raised / p.goal) * 100));
            return (
              <article key={p.id} className="group overflow-hidden rounded-3xl border border-emerald-100 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl">
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img src={p.img} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-emerald-800">
                    <MapPin className="h-3 w-3" /> {p.state}
                  </div>
                </div>
                <div className="p-5">
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-emerald-700">{p.partner}</div>
                  <h3 className="mt-1 font-display text-xl font-black text-emerald-950">{p.title}</h3>
                  <p className="mt-2 line-clamp-2 text-sm text-foreground/70">{p.desc}</p>

                  <div className="mt-4">
                    <div className="h-2 w-full overflow-hidden rounded-full bg-emerald-100">
                      <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-500" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="mt-2 flex items-center justify-between text-xs">
                      <span className="font-bold text-emerald-950">${p.raised.toLocaleString()} MXN</span>
                      <span className="text-emerald-800/70">de ${p.goal.toLocaleString()}</span>
                    </div>
                    <div className="mt-1 text-[11px] text-foreground/60">{p.supporters.toLocaleString()} viajeros aportaron · {pct}% financiado</div>
                  </div>

                  <button className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald-700 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-emerald-800">
                    Donar a este proyecto <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ============================ CARBON CALCULATOR ============================ */

export function CarbonCalculator() {
  const [flights, setFlights] = useState(2);
  const [carKm, setCarKm] = useState(800);
  const [hotelNights, setHotelNights] = useState(4);

  // Approx kg CO2 per unit
  const co2 = useMemo(() => {
    const flightKg = flights * 250; // avg short flight per leg
    const carKg = carKm * 0.18;
    const hotelKg = hotelNights * 30;
    return Math.round(flightKg + carKg + hotelKg);
  }, [flights, carKm, hotelNights]);

  const treesToOffset = Math.ceil(co2 / 22); // 1 árbol ~22kg CO2/año

  return (
    <section className="bg-gradient-to-b from-emerald-900 to-emerald-950 py-16 text-white sm:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-10 max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-300/30 bg-emerald-300/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-emerald-300">
            <Calculator className="h-3 w-3" /> Huella de carbono
          </div>
          <h2 className="mt-4 font-display text-3xl font-black sm:text-5xl">Calcula y compensa tu viaje</h2>
          <p className="mt-3 text-white/70">Estima la huella de tu próxima experiencia y compénsala sembrando árboles en zonas verificadas.</p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
          <div className="rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur sm:p-8">
            <CalcRow icon={Plane} label="Vuelos (tramos)" value={flights} setValue={setFlights} max={20} step={1} suffix="" />
            <CalcRow icon={Car} label="Auto (km)" value={carKm} setValue={setCarKm} max={5000} step={50} suffix=" km" />
            <CalcRow icon={Building2} label="Noches de hotel" value={hotelNights} setValue={setHotelNights} max={30} step={1} suffix="" />
          </div>

          <div className="rounded-3xl border border-emerald-300/30 bg-gradient-to-br from-emerald-500/15 to-teal-500/10 p-6 backdrop-blur sm:p-8">
            <div className="text-xs font-semibold uppercase tracking-widest text-emerald-300">Tu huella estimada</div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="font-display text-6xl font-black sm:text-7xl">{co2.toLocaleString()}</span>
              <span className="text-lg text-white/70">kg CO₂</span>
            </div>
            <div className="mt-6 rounded-2xl bg-emerald-950/40 p-4">
              <div className="flex items-center gap-3">
                <TreePine className="h-8 w-8 text-emerald-300" />
                <div>
                  <div className="text-xs uppercase tracking-widest text-emerald-300">Para compensar</div>
                  <div className="font-display text-2xl font-black">{treesToOffset} árboles nativos</div>
                </div>
              </div>
            </div>
            <button className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 px-6 py-3 text-sm font-bold text-emerald-950 transition-transform hover:scale-[1.02]">
              Compensar ahora · ${treesToOffset * 35} MXN <ChevronRight className="h-4 w-4" />
            </button>
            <p className="mt-3 text-center text-[11px] text-white/60">$35 MXN por árbol · Plantación verificada con coordenadas GPS</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function CalcRow({ icon: Icon, label, value, setValue, max, step, suffix }: { icon: typeof Plane; label: string; value: number; setValue: (n: number) => void; max: number; step: number; suffix: string }) {
  return (
    <div className="mb-6 last:mb-0">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-white/90">
          <Icon className="h-4 w-4 text-emerald-300" /> {label}
        </div>
        <span className="rounded-full bg-emerald-500/20 px-3 py-0.5 text-xs font-bold text-emerald-200">{value}{suffix}</span>
      </div>
      <input
        type="range"
        min={0}
        max={max}
        step={step}
        value={value}
        onChange={(e) => setValue(Number(e.target.value))}
        className="w-full accent-emerald-400"
        aria-label={label}
      />
    </div>
  );
}

/* ============================ ACADEMY ============================ */

const COURSES = [
  { id: "biod", title: "Biodiversidad de México 101", level: "Intro", duration: "3h", lessons: 12, img: heroImg, by: "UNAM" },
  { id: "manglar", title: "Ecología de manglares", level: "Intermedio", duration: "5h", lessons: 18, img: mangroveImg, by: "CONABIO" },
  { id: "fotografia", title: "Fotografía de naturaleza", level: "Práctico", duration: "4h", lessons: 14, img: pineImg, by: "Mostrando México" },
  { id: "regen", title: "Turismo regenerativo", level: "Avanzado", duration: "6h", lessons: 22, img: plantingImg, by: "Pronatura" },
];

export function Academy() {
  return (
    <section className="bg-white py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-10 max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-emerald-800">
            <GraduationCap className="h-3 w-3" /> Academia Verde
          </div>
          <h2 className="mt-4 font-display text-3xl font-black text-emerald-950 sm:text-5xl">Aprende a viajar regenerando</h2>
          <p className="mt-3 text-foreground/70">Cursos gratuitos con biólogos, fotógrafos y guías indígenas certificados.</p>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {COURSES.map((c) => (
            <article key={c.id} className="group overflow-hidden rounded-3xl border border-emerald-100 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={c.img} alt={c.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-emerald-700/95 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                  <BookOpen className="h-3 w-3" /> {c.level}
                </div>
              </div>
              <div className="p-5">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-emerald-700">{c.by}</div>
                <h3 className="mt-1 font-display text-lg font-black text-emerald-950">{c.title}</h3>
                <div className="mt-3 flex items-center gap-3 text-xs text-foreground/70">
                  <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {c.duration}</span>
                  <span className="inline-flex items-center gap-1"><BookOpen className="h-3 w-3" /> {c.lessons} lecciones</span>
                </div>
                <button className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-800 transition-colors hover:bg-emerald-100">
                  Comenzar curso <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================ COMMUNITY ============================ */

const COMMUNITY = [
  { name: "María L.", country: "México", level: "Embajadora Verde", trees: 124, quote: "Viajar plantando árboles cambió mi forma de ver mi país.", img: heroImg },
  { name: "Tom S.", country: "Canadá", level: "Protector Verde", trees: 38, quote: "Cada experiencia me conectó con comunidades increíbles.", img: pineImg },
  { name: "Sofía R.", country: "España", level: "Explorador Verde", trees: 12, quote: "El certificado digital lo enmarqué en mi sala.", img: mangroveImg },
];

export function Community() {
  return (
    <section className="bg-emerald-50/40 py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-10 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-emerald-800">
              <Users className="h-3 w-3" /> Comunidad Verde
            </div>
            <h2 className="mt-4 font-display text-3xl font-black text-emerald-950 sm:text-5xl">Viajeros que están dejando huella</h2>
            <p className="mt-3 text-foreground/70">+12,500 viajeros de 47 países han contribuido a restaurar ecosistemas en México.</p>
          </div>
          <div className="flex gap-3">
            <Stat label="Viajeros" value="12,540" />
            <Stat label="Países" value="47" />
            <Stat label="Árboles" value="38,210" />
          </div>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {COMMUNITY.map((c) => (
            <article key={c.name} className="rounded-3xl border border-emerald-100 bg-white p-6 shadow-sm">
              <div className="flex items-center gap-3">
                <img loading="lazy" decoding="async" src={c.img} alt={c.name} className="h-12 w-12 rounded-full object-cover" />
                <div>
                  <div className="font-display text-lg font-black text-emerald-950">{c.name}</div>
                  <div className="text-xs text-foreground/60">{c.country} · {c.level}</div>
                </div>
              </div>
              <p className="mt-4 text-sm italic text-foreground/80">"{c.quote}"</p>
              <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800">
                <TreePine className="h-3 w-3" /> {c.trees} árboles financiados
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-emerald-100 bg-white px-4 py-2 text-center">
      <div className="font-display text-2xl font-black text-emerald-950">{value}</div>
      <div className="text-[10px] font-semibold uppercase tracking-widest text-emerald-700">{label}</div>
    </div>
  );
}

/* ============================ PARTNERS / NGOs ============================ */

const PARTNERS = [
  { name: "WWF México", role: "Conservación marina y terrestre" },
  { name: "Pronatura", role: "Áreas protegidas y reforestación" },
  { name: "CONABIO", role: "Biodiversidad y datos abiertos" },
  { name: "Reforestamos México", role: "Bosques y comunidades" },
  { name: "Sea Shepherd", role: "Protección de océanos" },
  { name: "UNAM Biología", role: "Investigación y educación" },
  { name: "Conservación Internacional", role: "Selvas tropicales" },
  { name: "Naturalia", role: "Especies en peligro" },
];

export function Partners() {
  return (
    <section className="bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-emerald-800">
              <HeartHandshake className="h-3 w-3" /> Aliados verificados
            </div>
            <h2 className="mt-4 font-display text-3xl font-black text-emerald-950 sm:text-4xl">Trabajamos con quienes ya protegen México</h2>
          </div>
          <Link to="/contact" className="text-sm font-semibold text-emerald-700 hover:underline">
            ¿Eres una ONG? Únete →
          </Link>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {PARTNERS.map((p) => (
            <div key={p.name} className="rounded-2xl border border-emerald-100 bg-emerald-50/40 p-4 transition-colors hover:bg-emerald-50">
              <div className="flex items-center gap-2 text-emerald-950">
                <Sparkles className="h-4 w-4 text-emerald-600" />
                <div className="font-display text-base font-black">{p.name}</div>
              </div>
              <div className="mt-1 text-xs text-foreground/60">{p.role}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================ DONATIONS ============================ */

export function DonationCTA() {
  const [amount, setAmount] = useState(250);
  const presets = [100, 250, 500, 1000];
  const trees = Math.floor(amount / 35);
  return (
    <section className="bg-gradient-to-br from-emerald-700 via-emerald-800 to-teal-900 py-16 text-white sm:py-20">
      <div className="mx-auto max-w-5xl px-5 sm:px-8">
        <div className="grid gap-8 lg:grid-cols-[1fr_1fr] lg:items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-300/30 bg-emerald-300/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-emerald-200">
              <Award className="h-3 w-3" /> Donación directa
            </div>
            <h2 className="mt-4 font-display text-3xl font-black sm:text-5xl">Dona árboles, recibe tu certificado</h2>
            <p className="mt-3 text-white/75">100% transparente. Cada donación se traduce en árboles plantados con coordenadas GPS verificadas.</p>
            <div className="mt-6 flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2"><TrendingUp className="h-4 w-4 text-emerald-300" /> Reporte trimestral</div>
              <div className="flex items-center gap-2"><Globe2 className="h-4 w-4 text-emerald-300" /> Verificable en blockchain</div>
            </div>
          </div>

          <div className="rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur sm:p-8">
            <div className="text-xs font-semibold uppercase tracking-widest text-emerald-200">Selecciona un monto</div>
            <div className="mt-3 grid grid-cols-4 gap-2">
              {presets.map((p) => (
                <button
                  key={p}
                  onClick={() => setAmount(p)}
                  className={`rounded-full px-3 py-2 text-sm font-bold transition-colors ${
                    amount === p ? "bg-emerald-400 text-emerald-950" : "border border-white/20 bg-white/5 text-white hover:bg-white/10"
                  }`}
                >
                  ${p}
                </button>
              ))}
            </div>
            <div className="mt-4">
              <label className="text-xs text-white/70" htmlFor="custom-amount">Monto personalizado (MXN)</label>
              <input
                id="custom-amount"
                type="number"
                min={35}
                value={amount}
                onChange={(e) => setAmount(Math.max(0, Number(e.target.value)))}
                className="mt-1 w-full rounded-full border border-white/30 bg-white/10 px-4 py-2.5 text-sm text-white outline-none placeholder:text-white/75 focus:border-emerald-300"
              />
            </div>
            <div className="mt-4 flex items-center justify-between rounded-2xl bg-emerald-950/50 p-3 text-sm">
              <div className="flex items-center gap-2"><TreePine className="h-5 w-5 text-emerald-300" /> Equivale a</div>
              <div className="font-display text-xl font-black">{trees} árboles</div>
            </div>
            <button className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald-400 px-6 py-3 text-sm font-bold text-emerald-950 transition-transform hover:scale-[1.02]">
              Donar ${amount.toLocaleString()} MXN <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
